import dayjs from "dayjs";
import { Entry, ResponseFields } from "@dreambigger/shared/src/types";
import { ADDRESS_SUBKEY_SUFFIX_SET } from "./addressSubkeys";

/** Internal (underscore-prefixed) keys that must survive persistence so the
 * hook can rehydrate state on reload. Anything else underscore-prefixed is
 * treated as ephemeral UI state and stripped. */
const PERSISTED_INTERNAL_FIELDS = new Set(["_autoPopIndex"]);

const serializeValue = (value: unknown): unknown =>
  dayjs.isDayjs(value) ? value.format("YYYY-MM-DD") : value;

const shouldPersistKey = (key: string): boolean => {
  if (key === "id") return false;
  if (!key.startsWith("_")) return true;
  return PERSISTED_INTERNAL_FIELDS.has(key);
};

/** Inject the row index into a key, placing it before any trailing address
 *  subkey suffix so repeats mode emits `base__<i>__<suffix>` (v1 layout)
 *  instead of `base__<suffix>__<i>`. Non-address keys are appended at the end. */
const keyForIndex = (key: string, index: number): string => {
  const lastSep = key.lastIndexOf("__");
  if (lastSep > 0) {
    const suffix = key.slice(lastSep + 2);
    if (ADDRESS_SUBKEY_SUFFIX_SET.has(suffix)) {
      return `${key.slice(0, lastSep)}__${index}__${suffix}`;
    }
  }
  return `${key}__${index}`;
};

/**
 * Flatten Entry[] into the custom-step response shape the backend expects,
 * so the collection variant saves data in the same format as a multi-row
 * `custom` step with `allowRepeats: true`:
 *
 *   `{ fieldKey__<index>: value, ... }` (address subkeys:
 *   `fieldKey__<index>__<suffix>`)
 *
 * `id` is dropped (ephemeral UI state). `_autoPopIndex` is preserved under the
 * same suffixing convention so reload logic can tell auto-populated entries
 * from manual ones.
 */
export default function entriesToResponseFields(
  entries: Entry[]
): ResponseFields {
  const out: ResponseFields = {};
  entries.forEach((entry, index) => {
    for (const [key, value] of Object.entries(entry)) {
      if (!shouldPersistKey(key) || value === undefined) continue;
      out[keyForIndex(key, index)] = serializeValue(
        value
      ) as ResponseFields[string];
    }
  });
  return out;
}
